import ENV from 'constants/env';
import { DEFAULT_BRANDING } from 'hooks/useBranding';

const RESERVED = new Set([
    'www', 'api', 'app', 'mail', 'ftp', 'admin', 'portal', 'cdn', 'static',
    'onboarding', 'dev', 'staging', 'test',
]);

const APEX = new Set(['payrollnoot.com', 'www.payrollnoot.com', 'localhost', '127.0.0.1']);

const normalizeApiBaseUrl = (raw) => {
    if (!raw || typeof raw !== 'string') return raw;
    const trimmed = raw.trim().replace(/\/+$/, '');
    if (!trimmed) return raw;
    if (trimmed.endsWith('/api')) return trimmed;
    return `${trimmed}/api`;
};

/** Hosts that should keep PayrollNoot default branding (no API lookup). */
export function shouldSkipDomainBranding(hostname = window.location.hostname) {
    const host = (hostname || '').toLowerCase().split(':')[0];
    if (!host || APEX.has(host)) return true;
    if (host.endsWith('.payrollnoot.com')) {
        const label = host.slice(0, -'.payrollnoot.com'.length);
        if (!label || label.includes('.') || RESERVED.has(label)) return true;
        return false;
    }
    // Unknown host / custom domain — try API
    return false;
}

export function applyBrandingObject(branding) {
    const b = { ...DEFAULT_BRANDING, ...branding };
    localStorage.setItem('branding', JSON.stringify({
        productName: b.productName,
        primaryColor: b.primaryColor,
        darkColor: b.darkColor,
        logoUrl: b.logoUrl || null,
        loginLogoUrl: b.loginLogoUrl || null,
        faviconUrl: b.faviconUrl || null,
        firmId: branding.firmId ?? null,
        subdomain: branding.subdomain ?? null,
    }));
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', b.primaryColor);
    root.style.setProperty('--brand-dark', b.darkColor);
    root.style.setProperty('--brand-primary-light', b.primaryColor + '18');
    root.style.setProperty('--brand-primary-border', b.primaryColor + '40');
    document.title = b.productName;
    if (b.faviconUrl) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = b.faviconUrl;
    }
    window.dispatchEvent(new Event('branding-updated'));
    return b;
}

/**
 * Fetch branding for the current hostname (no auth). Returns branding object or null.
 */
export async function resolveDomainBranding(hostname = window.location.hostname) {
    const host = (hostname || '').toLowerCase().split(':')[0];
    if (shouldSkipDomainBranding(host)) return null;

    const base = normalizeApiBaseUrl(ENV.BASE_URL);
    if (!base) return null;

    try {
        const url = `${base}/branding?host=${encodeURIComponent(host)}`;
        const res = await fetch(url, {
            headers: {
                'X-API-key': ENV.API_KEY || '',
                Accept: 'application/json',
            },
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (!data || (!data.productName && !data.primaryColor)) return null;
        return applyBrandingObject(data);
    } catch {
        return null;
    }
}

/** Clear session auth keys but re-apply domain branding for white-label login. */
export async function clearAuthKeepDomainBranding() {
    [
        'authToken', 'userRole', 'companyId', 'employeeId', 'contractorId',
        'userEmail', 'userName', 'firmId', 'firmRoleId', 'firmRoleName',
        'userFeatures', 'userCompanies', 'locationId', 'locationName',
        'branding',
    ].forEach((key) => localStorage.removeItem(key));
    await resolveDomainBranding();
}
